import { useEffect, useRef } from "react"
import { useHistory, useLocation } from "react-router-dom"
import {
  getFormStateStorageKey,
  mergeStoredFormValues,
  readStoredFormState,
  type FormStateKey
} from "../form/state/useFormStateStorage"

/**
 * Subpanels: a panel opened from a parent panel's form that hands a result
 * back to it, e.g. "Create a new Cohort…" from a New offer panel.
 *
 * Only one panel route renders at a time, so the parent form unmounts while
 * the subpanel is open. The parent keeps its draft by using
 * `FormikWithDiscardFormState` with a stable `formStateKey`, which stores the
 * draft on unmount and restores it on mount. The subpanel merges its result
 * into that stored draft, then returns.
 *
 * Parent: `const openSubpanel = useOpenAugmentationSubpanel()` and
 * `openSubpanel("/parent/path/child")`.
 * Subpanel: `useAugmentationSubpanelReturn({ parentPath, parentFormStateKey,
 * parentInitialValues })`, then `returnWith({ field: value })` or `cancel()`.
 */

type SubpanelLocationState = {
  augmentationSubpanelParentPath?: string
}

export function useOpenAugmentationSubpanel() {
  const history = useHistory()
  const location = useLocation()

  return (subpanelPath: string) => {
    const state: SubpanelLocationState = {
      augmentationSubpanelParentPath: location.pathname
    }
    history.push(subpanelPath, state)
  }
}

export function useAugmentationSubpanelReturn<T extends object>({
  parentPath,
  parentFormStateKey,
  parentInitialValues
}: {
  parentPath: string
  parentFormStateKey: FormStateKey
  /** The parent form's initial values, used when it has no stored draft. */
  parentInitialValues: T
}) {
  const history = useHistory<SubpanelLocationState | undefined>()
  const location = useLocation<SubpanelLocationState | undefined>()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const openedFromParent =
    location.state?.augmentationSubpanelParentPath === parentPath

  // An async result belongs to this visit, even if the same route is reopened.
  // The host can also unmount without changing its detached memory history.
  function isCurrentSubpanel() {
    return (
      mounted.current &&
      history.location.key === location.key &&
      history.location.pathname === location.pathname
    )
  }

  function returnToParent() {
    if (!isCurrentSubpanel()) return
    // Going back keeps the panel history free of the finished subpanel, so
    // the panel's back control doesn't reopen it. A subpanel reached directly
    // (e.g. a shared link) has no parent entry to go back to.
    if (openedFromParent) {
      history.goBack()
    } else {
      history.replace(parentPath)
    }
  }

  return {
    /** The parent's current draft, e.g. for prefilling subpanel fields. */
    readParentDraft(): T {
      const stored = readStoredFormState<T>(
        getFormStateStorageKey(parentFormStateKey)
      )
      return { ...parentInitialValues, ...(stored?.values || {}) }
    },
    /** Merge `patch` into the parent's draft, then return to the parent. */
    returnWith(patch: Partial<T>) {
      if (!isCurrentSubpanel()) return
      mergeStoredFormValues<T>(parentFormStateKey, patch, parentInitialValues)
      returnToParent()
    },
    /** Return to the parent with its draft unchanged. */
    cancel: returnToParent
  }
}
